// src/api/dashboardReports.ts
//
// Creating a report and listing the templates it can start from (plan
// 2026-10-27-the-old-report-kind-is-removed, step 1). A report is a dashboard
// of kind `report` (ADR-0496 D1): `POST /reports` births one and answers that
// dashboard, `GET /reports/templates` lists the New report modal's cards.
// Moved here from `api/reports.ts`, which goes with the old report kind.

import type { ReportAudience, ReportPurpose } from "@/constants/reportBrief";
import { apiJson } from "./client";
import type { DashboardDTO } from "./dashboards";

export async function createReport(body: {
  workspace_public_id: string;
  title: string;
  description?: string;
  /** Registry key when the report starts from a template. The backend
   * validates it against the registry, so an unknown key is a 422 rather
   * than a silently stored string. Omitted for "start from scratch". */
  template_key?: string;
  /** Create the report as a composition of blocks. Every report started from
   * the New report modal is one of these: with a `template_key` of a BLOCK
   * template the server instantiates that recipe, without one it is an empty
   * report. `period` is required unless a block template supplies it. Since
   * 2026-09-26 this needs no capability; it is a workspace-EDITOR action like
   * any other create. Since ADR-0495 it is the only kind made: a create
   * without it is refused with 409 `report_kind_retired`. */
  composed: true;
  /** The composed report's window, a period preset. Required with
   * `composed`; the backend validates it against the preset table. */
  period?: string;
  /** The page format of a composed report (plan 2026-09-08-report-editor-
   * is-the-report-itself, A1/C9). Omitted = portrait. */
  page_format?: "portrait" | "landscape";
  /** Where the create came from — operator logs only, no behaviour. */
  origin?: "ui" | "assistant";
  /** What this report compares its numbers against. Omit to use the
   * template's own choice — that is what every report did before this
   * setting existed, so omitting it is the default, not a gap.
   *
   * `"target"` is NOT a value here: targets do not exist yet. The backend
   * rejects it, so a client that sends it anyway fails loudly rather than
   * silently comparing to something else. */
  compare_mode?: "previous_period" | "same_period_last_year" | "target";
  /** Report brief (plan 2026-08-28): who the report is for. Optional on the
   * wire so older clients keep working during the expand/contract rollout —
   * the drawer nonetheless requires it, because a brief nobody fills in is
   * not a brief. Keys come from `api/constants/report_brief.py`. */
  audience?: ReportAudience;
  /** Report brief: what the report has to do. Same contract as `audience`. */
  purpose?: ReportPurpose;
  /** Sources the report may draw on. OMITTED (never `[]`) for "Auto" — an
   * empty list and an absent field mean the same thing to the backend, and
   * sending one of them consistently keeps the payload readable. Unknown or
   * foreign ids are a 400, so this must carry ids the account owns. */
  source_public_ids?: string[];
}): Promise<DashboardDTO> {
  // A report is born as a dashboard of kind `report` (ADR-0496 D1, plan
  // 2026-10-22 step A5): the answer is that dashboard.
  return apiJson(`/reports`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** One card in the New report modal, from the BLOCK template registry.
 *
 * `available` is per CALLER, not per account: the backend computes it from the
 * same grant-filtered catalogue read the create itself performs, so an offered
 * card cannot be refused by the create and a greyed card would not have
 * worked. `missing_packs` carries pack LABELS ("Ecom Performance"), ready to
 * show, because the customer reads them. */
export type BlockTemplateCardDTO = {
  key: string;
  version: number;
  label: string;
  description: string;
  period: string;
  page_format: "portrait" | "landscape";
  compare_mode: string | null;
  audience: string;
  available: boolean;
  missing_packs: string[];
};

export async function listReportTemplates(): Promise<{
  block_templates: BlockTemplateCardDTO[];
}> {
  return apiJson(`/reports/templates`);
}
